import { useMemo, useState } from 'react'
import {
  Alert,
  App,
  Button,
  Card,
  Form,
  Input,
  Modal,
  Radio,
  Select,
  Space,
  Table,
  Tag,
  Typography,
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { Can } from '@/shared/auth/Can'
import { usePermissions } from '@/shared/auth/usePermissions'
import { formatDateTime, utcTooltip } from '@/shared/lib/format'
import { submitOnValid } from '@/shared/lib/submitOnValid'
import { RowActions } from '@/shared/ui/table/RowActions'
import { updateSubscriber, type Subscriber } from '@/features/subscriber/subscriberApi'
import {
  allocateNextIp,
  allocateSpecificIp,
  fetchAllocationsByOwner,
  fetchIpPools,
  releaseIpAllocation,
  type IpAllocation,
} from './ipPoolApi'

interface SubscriberIpAllocationsProps {
  /**
   * ⚠️ TO'LIQ YOZUV, faqat `id` va `staticIp` EMAS.
   *
   * "Statik IP qilib qo'yish" amali `PUT /api/subscribers/{id}` ni
   * chaqiradi, backend esa uni TO'LIQ ALMASHTIRISH deb bajaradi
   * (`subscriberApi.SubscriberDraft` javadoc'i): `phone` `COALESCE` SIZ
   * yoziladi, ya'ni yuborilmasa O'CHADI. Ya'ni bu amal uchun mavjud
   * maydonlarning hammasi qo'lda bo'lishi SHART — kartochka ularni
   * allaqachon yuklagan, ikkinchi so'rov keraksiz.
   */
  subscriber: Subscriber
  /** Yopilgan abonentda o'zgartirish tugmalari chizilmaydi */
  disabled?: boolean
}

type AllocateMode = 'next' | 'specific'

interface AllocateForm {
  poolId: string
  mode: AllocateMode
  ip?: string
}

/**
 * Abonentga fonddan ajratilgan IP manzillar — INVENTAR hisobi.
 *
 * <h2>⚠️ NEGA BU KARTA UMUMAN PAYDO BO'LDI</h2>
 * `xsib-inventory` da manzil ajratish va bo'shatishning to'liq API'si
 * bor edi (`POST .../allocations`, `.../allocations/next`,
 * `POST /api/inventory/ip-allocations/{id}/release`,
 * `GET .../by-owner`), lekin panelda ularni chaqiradigan BIRORTA tugma
 * yo'q edi. Ya'ni operator fond yaratardi, "shu IP kimda" degan savolga
 * javob olardi — va manzil BERA OLMASDI. Fond bo'shligicha qolar,
 * modulning butun maqsadi bajarilmasdi.
 *
 * <h2>⚠️ ENG MUHIM OGOHLANTIRISH — IKKI XIL "STATIK IP" BOR</h2>
 * Bu ikkalasi BOG'LANMAGAN va ularni bitta narsa deb ko'rsatish eng
 * qimmat turdagi xatoni tug'diradi:
 *
 * <ul>
 *   <li><b>Abonent yozuvidagi {@code staticIp}</b> — RADIUS shuni
 *       {@code Framed-IP-Address} qilib qaytaradi
 *       ({@code DefaultRadiusAuthorizeService}). Abonent HAQIQATDA shu
 *       manzilni oladi. U "Tahrirlash" oynasida o'zgaradi;</li>
 *   <li><b>Fond ajratmasi</b> (shu karta) — bu faqat REYESTR: "shu
 *       manzil band, egasi falonchi". U RADIUS'ga HECH NARSA
 *       yubormaydi.</li>
 * </ul>
 *
 * Server tomonda ularni bog'laydigan kod yo'q va bu ataylab:
 * {@code xsib-inventory} {@code xsib-subscriber} ga murojaat qilmaydi
 * (docs/00-ARCHITECTURE.md — servis boshqa servisning sxemasiga
 * tegmaydi). Demak MUVOFIQLIKNI ODAM ta'minlaydi, va panelning vazifasi
 * — nomuvofiqlikni KO'RSATISH, uni yashirish emas. Shuning uchun karta
 * ikki holatni alohida tekshiradi:
 *
 * <ul>
 *   <li>{@code staticIp} bor, lekin reyestrda ajratilmagan → manzil
 *       "bo'sh" ko'rinadi va boshqa abonentga berilib ketishi mumkin;</li>
 *   <li>reyestrda ajratma bor, lekin {@code staticIp} boshqa/yo'q →
 *       manzil band turibdi, lekin abonent uni olmaydi.</li>
 * </ul>
 *
 * Ikkalasi ham OGOHLANTIRISH (sariq), xato emas: holat ishlab turgan
 * tizimda ham qonuniy bo'lishi mumkin (masalan manzil kommutator uchun
 * zaxiraga olingan). Panel yolg'on aytmaydi va qaror qabul qilmaydi —
 * faktni ko'rsatadi.
 */
export function SubscriberIpAllocations({
  subscriber,
  disabled,
}: SubscriberIpAllocationsProps) {
  const { t } = useTranslation()
  /*
    ⚠️ BUG-048 — `modal` KONTEKSTDAN: statik `Modal.confirm`
    `ConfigProvider` mavzusiga yetmaydi va qorong'i panel ustida OQ
    dialog chizadi.
  */
  const { modal } = App.useApp()
  const queryClient = useQueryClient()
  const { canAny } = usePermissions()
  const [error, setError] = useState<string | null>(null)
  const [open, setOpen] = useState(false)
  /** Statik IP endigina o'rnatildimi — natija ATAYLAB sariq (§3.2, Qoida 4) */
  const [assigned, setAssigned] = useState(false)
  const [form] = Form.useForm<AllocateForm>()

  const subscriberId = subscriber.id
  /**
   * Abonent yozuvidagi `staticIp` — RADIUS AYNAN shuni beradi.
   * `null` = individual manzil yo'q, NAS pooldan oladi.
   */
  const staticIp = subscriber.staticIp ?? null

  /*
    ⚠️ `mode` ni `Form.useWatch` bilan kuzatamiz, alohida `useState`
    bilan emas: oyna `destroyOnHidden` bilan yopiladi va ikkinchi
    ochilishda `useState` eski qiymatni saqlab qolardi, forma esa
    tozalangan bo'lardi — ya'ni ko'rinish va ma'lumot ajralib ketardi.
  */
  const mode = Form.useWatch('mode', form) ?? 'next'

  const canRead = canAny('inventory.ip.read', 'inventory.ip.manage')

  const allocations = useQuery({
    queryKey: queryKeys.ipPools.byOwner('SUBSCRIBER', subscriberId),
    queryFn: () => fetchAllocationsByOwner('SUBSCRIBER', subscriberId),
    // ⚠️ Huquqsiz foydalanuvchida so'rov UMUMAN yuborilmaydi. 403 ni
    // qaytarib "xato" deb ko'rsatish operatorni nosozlik izlashga
    // yuborardi — aslida javob to'g'ri va kutilgan.
    enabled: canRead,
    retry: false,
  })

  /*
    Fondlar ro'yxati jadval uchun ham (ajratma qaysi fonddan), oyna
    uchun ham kerak.

    ⚠️ Kalit `queryKeys.ipPools.all` — `IpPoolsPage` bilan AYNAN bir
    xil. Ya'ni operator "IP fondlari" sahifasidan kelgan bo'lsa qayta
    so'rov ketmaydi, TanStack keshdan beradi. Alohida kalit yozish
    bir xil ma'lumotni ikki marta tortardi.
  */
  const pools = useQuery({
    queryKey: queryKeys.ipPools.all,
    queryFn: fetchIpPools,
    enabled: canRead,
    retry: false,
  })

  const poolName = (poolId: string | null | undefined) =>
    pools.data?.find((p) => p.id === poolId)?.name ?? '—'

  const refresh = () =>
    void queryClient.invalidateQueries({
      queryKey: queryKeys.ipPools.byOwner('SUBSCRIBER', subscriberId),
    })

  /*
    ⚠️ AJRATISH XATOSI KARTAGA EMAS, OYNAGA yoziladi (`VoucherVoidModal`
    naqshi). Ilgari u karta bannerida chiqardi, oyna esa OCHIQ qolardi —
    xabar modal niqobi ortida turardi. Bu yerda u ayniqsa muhim: "manzil
    band" degan 409 aynan boshqa IP tanlash uchun kerak, ya'ni forma
    bilan bir ekranda bo'lishi shart.

    Karta banneri QOLADI — `release` oyna ichida emas (`Popconfirm`).
  */
  const allocate = useMutation({
    mutationFn: (values: AllocateForm) =>
      values.mode === 'specific'
        ? allocateSpecificIp(values.poolId, (values.ip ?? '').trim(), 'SUBSCRIBER', subscriberId)
        : allocateNextIp(values.poolId, 'SUBSCRIBER', subscriberId),
    onSuccess: () => {
      setOpen(false)
      form.resetFields()
      setError(null)
      refresh()
    },
  })

  const closeAllocate = () => {
    allocate.reset()
    setOpen(false)
  }

  const release = useMutation({
    mutationFn: (allocationId: string) => releaseIpAllocation(allocationId),
    onSuccess: () => {
      setError(null)
      refresh()
    },
    onError: (err) => setError(translateError(err, t)),
  })

  /*
    ⚠️ REYESTR BILAN RADIUS ORASIDAGI KO'PRIK — SHU AMAL.

    Sinf javadoc'ida yozilganidek, ajratma va `subscriber.staticIp` ikki
    BOSHQA narsa va server tomonda ularni bog'laydigan kod ATAYLAB yo'q
    (`xsib-inventory` `xsib-subscriber` ga tegmaydi). Lekin panel shu
    kungacha nomuvofiqlikni faqat KO'RSATARDI, uni tuzatadigan yo'lni
    bermasdi: operator manzilni band qilib, keyin kartochkaning
    «Tahrirlash» oynasini ochib, o'sha manzilni QO'LDA ko'chirib
    yozishi kerak edi. Tashqi tekshiruvchi aynan shu bo'shliqni topdi
    («There is no action to assign a reserved IP to the subscriber»).

    ⚠️ AVTOMATIK QILINMADI. «Biriktirdim → darhol statik IP bo'lsin»
    degan xulq bir necha manzil biriktirilganda oxirgisini jimgina
    g'olib qilardi, va abonent kutilmaganda boshqa manzilga ko'chardi.
    Shuning uchun bu ALOHIDA, ATAYLAB bosiladigan tugma.

    ⚠️ Huquq `subscriber.update` — chunki amal AYNAN abonent yozuvini
    o'zgartiradi, IP reyestrini emas. `inventory.ip.manage` yetarli deb
    hisoblash IP reyestrini yurituvchi xodimga abonent kartochkasini
    tahrirlash huquqini jimgina berardi.
  */
  const assignStatic = useMutation({
    mutationFn: (ip: string) =>
      updateSubscriber(subscriberId, {
        login: subscriber.login,
        displayName: subscriber.displayName ?? null,
        // ⚠️ HAR SAFAR yuboriladi: backend `phone` ni `COALESCE` siz
        // yozadi, ya'ni yubormaslik raqamni O'CHIRARDI
        // (`EditSubscriberModal` dagi bilan bir xil qoida).
        phone: subscriber.phone ?? null,
        kind: subscriber.kind,
        ownerPartyId: subscriber.ownerPartyId ?? null,
        agentId: subscriber.agentId ?? null,
        simultaneousUse: subscriber.simultaneousUse ?? null,
        staticIp: ip,
      }),
    onSuccess: () => {
      setError(null)
      setAssigned(true)
      void queryClient.invalidateQueries({ queryKey: queryKeys.subscribers.detail(subscriberId) })
      void queryClient.invalidateQueries({ queryKey: queryKeys.subscribers.all })
    },
    onError: (err) => setError(`${t('ipAllocation.assignStaticFailed')}: ${translateError(err, t)}`),
  })

  /*
    ⚠️ `useMemo` — `?? []` NI TO'G'RIDAN-TO'G'RI YOZIB BO'LMAYDI: har
    renderda yangi massiv yasalardi va uni quyidagi `useMemo` ning
    bog'liqligi qilish memoizatsiyani butunlay bekor qilardi (eslint
    `react-hooks/exhaustive-deps` aynan shuni ogohlantiradi).
  */
  const rows = useMemo(() => allocations.data ?? [], [allocations.data])

  /*
    ⚠️ Tekshiruv FAQAT ma'lumot KELGANDA bajariladi (`isSuccess`).
    So'rov hali ketayotganda `rows` bo'sh bo'ladi va tekshiruv
    "reyestrda yo'q" degan YOLG'ON ogohlantirishni bir soniyaga
    ko'rsatib yuborardi — operator uni o'qib ulgurishi mumkin.
  */
  const mismatch = useMemo(() => {
    if (!allocations.isSuccess) return null
    const held = rows.map((r) => r.ip)
    if (staticIp && !held.includes(staticIp)) return 'staticNotReserved' as const
    if (!staticIp && held.length > 0) return 'reservedNotStatic' as const
    return null
  }, [allocations.isSuccess, rows, staticIp])

  const columns: ColumnsType<IpAllocation> = [
    {
      // Ro'yxat sahifalanmaydi (`pagination={false}`) — raqam `index` dan.
      key: '__rowNo',
      title: t('dataTable.rowNo'),
      width: 56,
      align: 'center',
      render: (_v, _r, index) => <Typography.Text type="secondary">{index + 1}</Typography.Text>,
    },
    {
      key: 'ip',
      title: t('ipAllocation.address'),
      render: (_, row) => (
        <Space size={4}>
          <Typography.Text copyable code>
            {row.ip}
          </Typography.Text>
          {/* Reyestrdagi manzil RADIUS beradigan manzil bilan bir xil
              bo'lsa buni AYTISH kerak: aks holda operator ikki
              ro'yxatni ko'z bilan solishtirishga majbur bo'ladi */}
          {staticIp === row.ip ? <Tag color="green">{t('ipAllocation.matchesStatic')}</Tag> : null}
        </Space>
      ),
    },
    {
      key: 'pool',
      title: t('ipAllocation.pool'),
      width: 160,
      render: (_, row) => poolName(row.poolId),
    },
    {
      key: 'allocatedAt',
      title: t('ipPool.allocatedAt'),
      width: 170,
      render: (_, row) => (
        <Typography.Text title={utcTooltip(row.allocatedAt)}>
          {formatDateTime(row.allocatedAt)}
        </Typography.Text>
      ),
    },
    {
      key: 'actions',
      title: '',
      width: 56,
      align: 'center',
      fixed: 'right',
      render: (_, row) =>
        // Yopilgan abonentda amallar UMUMAN chizilmaydi (prop `disabled`).
        disabled ? null : (
          <RowActions
            items={[
              {
                /*
                  "Statik IP qilib qo'yish" — reyestrni RADIUS bilan
                  moslashtiruvchi YAGONA amal (mutatsiya izohiga qarang).

                  ⚠️ Allaqachon statik bo'lgan manzil uchun CHIZILMAYDI:
                  o'sha qatorda yashil «statik IP bilan bir xil» tegi
                  turadi, ya'ni amal hech narsa o'zgartirmasdi va
                  «bosdim, hech nima bo'lmadi» degan yo'l bo'lardi.

                  ⚠️ Huquq `subscriber.update` — `inventory.ip.manage` EMAS.
                */
                key: 'assignStatic',
                label: t('ipAllocation.assignStatic'),
                onClick: () => {
                  modal.confirm({
                    title: t('ipAllocation.assignStaticTitle', { ip: row.ip }),
                    content: t('ipAllocation.assignStaticHint'),
                    okText: t('ipAllocation.assignStatic'),
                    cancelText: t('common.cancel'),
                    onOk: () => assignStatic.mutate(row.ip),
                  })
                },
                permission: 'subscriber.update',
                hidden: row.ip === staticIp,
                loading: assignStatic.isPending && assignStatic.variables === row.ip,
              },
              {
                /*
                  ⚠️ Tasdiq so'raladi: bo'shatilgan manzil darhol boshqa
                  abonentga berilishi mumkin va uni "orqaga qaytarish"
                  amali yo'q — eski ajratma yopilgan, yangisi ochiladi.
                */
                key: 'release',
                label: t('ipAllocation.release'),
                onClick: () => {
                  modal.confirm({
                    title: t('ipAllocation.releaseConfirm'),
                    content: t('ipAllocation.releaseConfirmHint'),
                    okText: t('ipAllocation.release'),
                    cancelText: t('common.cancel'),
                    onOk: () => release.mutate(row.id),
                  })
                },
                permission: 'inventory.ip.manage',
                danger: true,
                divider: true,
                loading: release.isPending,
              },
            ]}
          />
        ),
    },
  ]

  const activePools = useMemo(
    () =>
      (pools.data ?? [])
        // ⚠️ O'chirilgan fonddan manzil ajratib bo'lmaydi. Uni
        // ro'yxatda qoldirish mavjud bo'lmagan tanlovni taklif
        // qilardi (`SubscriberFundAction` dagi BUG-07 bilan bir xil).
        .filter((p) => p.state === 'ACTIVE')
        .map((p) => ({ value: p.id, label: `${p.name} — ${p.cidr}` })),
    [pools.data],
  )

  return (
    <Card
      title={t('ipAllocation.title')}
      extra={
        !disabled && activePools.length > 0 ? (
          <Can permission="inventory.ip.manage">
            <Button size="small" onClick={() => setOpen(true)}>
              {t('ipAllocation.allocate')}
            </Button>
          </Can>
        ) : null
      }
    >
      <Typography.Paragraph type="secondary" style={{ marginBottom: 16 }}>
        {t('ipAllocation.explain')}
      </Typography.Paragraph>

      {!canRead ? (
        <Alert type="info" showIcon message={t('ipAllocation.noPermission')} />
      ) : (
        <>
          {error ? (
            <Alert
              type="error"
              showIcon
              closable
              message={error}
              onClose={() => setError(null)}
              style={{ marginBottom: 16 }}
            />
          ) : null}

          {/*
            ⚠️ ATAYLAB `success` EMAS, `warning`: yozuv o'zgardi, lekin
            HOZIRGI faol sessiya eski manzilda qoladi (CLAUDE.md §3.2,
            Qoida 4 — yashil belgi "hammasi tayyor" degan yolg'on
            taassurot berardi va operator abonentga shunday aytardi).
          */}
          {assigned ? (
            <Alert
              type="warning"
              showIcon
              closable
              style={{ marginBottom: 16 }}
              message={t('ipAllocation.assignStaticDone')}
              onClose={() => setAssigned(false)}
            />
          ) : null}

          {mismatch ? (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
              message={t(`ipAllocation.mismatch.${mismatch}`)}
              description={t(`ipAllocation.mismatch.${mismatch}Hint`, { ip: staticIp ?? '' })}
            />
          ) : null}

          {/* Fond umuman yaratilmagan bo'lsa "bo'sh jadval" sabab
              ko'rsatmaydi — operator "nega tugma yo'q?" deb qoladi */}
          {pools.isSuccess && activePools.length === 0 ? (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
              message={t('ipAllocation.noPools')}
              description={t('ipAllocation.noPoolsHint')}
            />
          ) : null}

          <Table<IpAllocation>
            columns={columns}
            dataSource={rows}
            rowKey={(row) => row.id}
            loading={allocations.isPending}
            size="small"
            pagination={false}
            locale={{ emptyText: t('ipAllocation.empty') }}
          />
        </>
      )}

      <Modal
        open={open}
        title={t('ipAllocation.allocateTitle')}
        onCancel={closeAllocate}
        onOk={() => submitOnValid(form, (values) => allocate.mutate(values))}
        confirmLoading={allocate.isPending}
        okText={t('ipAllocation.allocate')}
        cancelText={t('common.cancel')}
        destroyOnHidden
      >
        {/*
          ⚠️ Bu ogohlantirish OYNADA ham takrorlanadi. Kartadagi
          matnni operator o'qib bo'lgan bo'lishi mumkin, lekin qaror
          AYNAN shu yerda qabul qilinadi — "ajratdim, demak abonent
          shu IP ni oldi" degan xulosa aynan shu tugmani bosgandan
          keyin tug'iladi.
        */}
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message={t('ipAllocation.notRadiusTitle')}
          description={t('ipAllocation.notRadiusHint')}
        />

        {/*
          ⚠️ TAKRORIY BAND QILISH — TO'SILMAYDI, lekin KO'RSATILADI.

          Reyestr bir egaga bir necha manzilni ATAYLAB qo'llab-quvvatlaydi
          (`GET /by-owner` ro'yxat qaytaradi) va bu qonuniy holat:
          masalan bittasi jihoz uchun zaxiraga olinadi. Shuning uchun
          panelda qat'iy taqiq qo'yish ishlayotgan imkoniyatni
          yo'qotardi — serverdan qattiqroq bo'lish (`AgentSelect` dagi
          o'sha qaror).

          Lekin tashqi tekshiruvchi topgan holat boshqacha: u bir
          abonentga ketma-ket ikkita manzil band qildi va HECH QANDAY
          belgi ko'rmadi — ya'ni tasodifiy ikkilanish va ataylab qilingan
          zaxira ekranda BIR XIL ko'rinardi. Endi sanoq aytiladi va
          «statik IP faqat bitta» qoidasi shu yerda eslatiladi: amal
          ataylab qilinganiga aylanadi.
        */}
        {rows.length > 0 ? (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            message={t('ipAllocation.alreadyHeldTitle', { count: rows.length })}
            description={t('ipAllocation.alreadyHeldHint')}
          />
        ) : null}

        {/* 409 ("manzil band") SHU YERDA qoladi — boshqasini tanlash uchun */}
        {allocate.isError ? (
          <Alert
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
            message={translateError(allocate.error, t)}
          />
        ) : null}

        <Form
          form={form}
          layout="vertical"
          preserve={false}
          disabled={allocate.isPending}
          initialValues={{ mode: 'next' }}
        >
          <Form.Item
            name="poolId"
            label={t('ipAllocation.pool')}
            rules={[{ required: true, message: t('ipAllocation.poolRequired') }]}
          >
            <Select
              autoFocus
              showSearch
              optionFilterProp="label"
              options={activePools}
              placeholder={t('ipAllocation.poolPlaceholder')}
            />
          </Form.Item>

          <Form.Item name="mode" label={t('ipAllocation.mode')}>
            <Radio.Group>
              <Radio value="next">{t('ipAllocation.modeNext')}</Radio>
              <Radio value="specific">{t('ipAllocation.modeSpecific')}</Radio>
            </Radio.Group>
          </Form.Item>

          {mode === 'specific' ? (
            <Form.Item
              name="ip"
              label={t('ipAllocation.address')}
              extra={t('ipAllocation.addressHint')}
              rules={[{ required: true, message: t('ipAllocation.addressRequired') }]}
            >
              <Input placeholder="10.10.40.15" />
            </Form.Item>
          ) : null}
        </Form>
      </Modal>
    </Card>
  )
}
